/**
 * Shared Study-Cursor surface fetching (FXC-9009 adversarial-review fixes).
 *
 * ONE debounced slice spec + ONE canonical queryKey/grid for every
 * slice-driven /composite/surface consumer (2D board lanes, 3D trust lanes,
 * the campaign live-build). This is what makes N lanes × 1 pin-slider drag
 * coalesce into one request per output against the single-worker API:
 *
 *   - every consumer keys on `["study-surface", projectId, branch, quantity,
 *     spec.key]` with the SAME nx/ny grid — react-query dedupes concurrent
 *     observers onto one fetch;
 *   - the spec is trailing-debounced ≥150ms, so a 60 Hz drag issues zero
 *     requests until it quiets;
 *   - superseded fetches are ABORTED via react-query's signal (they must not
 *     queue behind each other on the single worker).
 */
import { useMemo } from "react";
import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";

import { fetchSurface, type SurfaceResponse } from "../../../api/composite";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import {
  useStudyCursor,
  type UseStudyCursorResult,
} from "../../adaptive-sampling/surfaces/useSliceState";
import type { AxisSpec } from "../../adaptive-sampling/types";
import { boardFixedValues } from "./exploreBoardModel";

/** The ONE canonical surface grid every study lane fetches (finding: three
 *  disjoint grids meant three cache-missing fetches of the same surface). */
export const STUDY_SURFACE_GRID = { nx: 36, ny: 24 } as const;
/** Trailing debounce on slice-driven refetches (§7 single-worker API). */
export const SLICE_DEBOUNCE_MS = 160;

export type StudySliceSpec = {
  xInput: string;
  yInput: string;
  fixed: Record<string, number>;
  /** Deterministic cache key (axes + sorted fixed values). */
  key: string;
};

function specKey(xInput: string, yInput: string, fixed: Record<string, number>): string {
  const fixedKey = Object.keys(fixed)
    .sort()
    .map((k) => `${k}:${Number(fixed[k]!.toFixed(6))}`)
    .join(",");
  return `x=${xInput}|y=${yInput}|fixed=${fixedKey}`;
}

export type UseDebouncedStudySliceResult = UseStudyCursorResult & {
  /** The debounced spec — key queries on THIS. */
  spec: StudySliceSpec;
  /** The live (undebounced) spec — for display only, never for fetching. */
  liveSpec: StudySliceSpec;
};

/**
 * The one slice-spec derivation every surface consumer shares: cursor →
 * fixed values → deterministic key → trailing debounce. Each mount debounces
 * independently but from the same store, so all lanes settle on the SAME key.
 */
export function useDebouncedStudySlice(
  projectId: string,
  branch: string,
  inputs: readonly AxisSpec[],
): UseDebouncedStudySliceResult {
  const cursorResult = useStudyCursor(projectId, branch, inputs);
  const { cursor } = cursorResult;
  const liveSpec = useMemo<StudySliceSpec>(() => {
    const fixed = boardFixedValues(inputs, cursor);
    return { xInput: cursor.xInput, yInput: cursor.yInput, fixed, key: specKey(cursor.xInput, cursor.yInput, fixed) };
    // fixed derives from exactly these cursor fields + the axis metadata
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, cursor.xInput, cursor.yInput, cursor.pinned]);
  const spec = useDebouncedValue(liveSpec, SLICE_DEBOUNCE_MS);
  return { ...cursorResult, spec, liveSpec };
}

/** The canonical queryKey — exported so tests can pin key unification. */
export function studySurfaceQueryKey(
  projectId: string,
  branch: string,
  quantity: string,
  spec: Pick<StudySliceSpec, "key">,
): readonly unknown[] {
  return ["study-surface", projectId, branch, quantity.toLowerCase(), spec.key] as const;
}

/**
 * The ONE surface query. Every lane showing quantity Q at the current slice
 * mounts this with the same key → one in-flight fetch, shared cache entry.
 */
export function useStudySurface(
  projectId: string,
  branch: string,
  spec: StudySliceSpec,
  quantity: string,
  opts: { enabled?: boolean; pollMs?: number | false } = {},
): UseQueryResult<SurfaceResponse> {
  const { enabled = true, pollMs = false } = opts;
  return useQuery({
    queryKey: studySurfaceQueryKey(projectId, branch, quantity, spec),
    queryFn: ({ signal }) =>
      fetchSurface({
        projectId,
        branch,
        quantity: quantity.toLowerCase(),
        axisX: spec.xInput,
        axisY: spec.yInput,
        nx: STUDY_SURFACE_GRID.nx,
        ny: STUDY_SURFACE_GRID.ny,
        fixed: spec.fixed,
        signal,
      }),
    enabled: enabled && !!spec.xInput && !!spec.yInput && spec.xInput !== spec.yInput,
    refetchInterval: pollMs,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
